import axios from "axios";
import { fetchCopilotAccessToken, getConversationID, getConversationMessages } from "../Generators/CopilotFunctions";

export async function CopilotExtractText(documentText: string, AIProcess: string): Promise<string | null> {
  const Copilot: any = JSON.parse(localStorage.getItem("Copilot") || "{}");
  const CopilotPAUrl = Copilot?.Url;
  const accessToken = await fetchCopilotAccessToken(CopilotPAUrl);
  const conversationId = await getConversationID(accessToken);

  if (!documentText?.trim()) {
    return "No meaningful content provided in the document text.";
  }

  let watermark: string | null = null;

  const fetchCopilotResponse = async (accessToken, conversationId) => {
    let botMessages = [];
    try {
      const botConversation = await getConversationMessages(accessToken, conversationId, watermark);
      botMessages =
        botConversation?.activities.filter((activity: any) => activity.from.id !== "user" && activity.text?.trim()) ||
        [];

      watermark = botConversation?.watermark;
      return { response: botMessages, hasMessages: botMessages.length > 0 };
    } catch (error) {
      console.error("Error fetching messages:", error);
      return { response: botMessages, hasMessages: false };
    }
  };

  const generateCopilot = async (accessToken, conversationId, copilotPrompt) => {
    try {
      await axios.post(
        `https://directline.botframework.com/v3/directline/conversations/${conversationId}/activities`,
        copilotPrompt,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const maxAttempts = 30;
      let attempt = 0;

      while (attempt < maxAttempts) {
        await new Promise((res) => setTimeout(res, 2000));

        const { response: messages, hasMessages } = await fetchCopilotResponse(accessToken, conversationId);

        if (hasMessages && messages.length > 0) {
          const lastMessage = messages[messages.length - 1];
          const text = lastMessage?.text?.trim();
          // console.log(text, "response text");
          if (text) {
            return text;
          }
        }

        attempt++;
      }

      return "Timed out waiting for bot response";
    } catch (error) {
      console.error("Error in generateCopilot:", error);
      return "Failed to generate copilot";
    }
  };

  if (AIProcess === "Missing Clause") {
    const truncatedText = documentText;
    const userInput = `
    Only extract the missing or not-found expected keys from the document.
    Return strictly as a valid raw JSON object, not as a string.
    The format must be:
    {
      "missing": {
        "1. <key_name>": "<short description of what this key represents>",
        "2. <key_name>": "<short description of what this key represents>"
      }
    }
    - Do not wrap the entire JSON object in quotes.
    - Do not return markdown code blocks like \`\`\`.
    - Do not include any explanations, summaries, or additional text.
    - Return only the JSON object, exactly as specified above.
    `.trim();

    const instruction = `You are a legal assistant AI.
      User instruction: "${userInput?.trim()}"
      Use the contract text below to respond accurately and only based on the instruction.
      Contract Text: ${truncatedText}`.trim();

    const Copilotmessage = {
      type: "message",
      from: { id: "user", name: "You" },
      text: instruction,
      time: new Date().toISOString(),
    };

    const copilotResponseText = await generateCopilot(accessToken, conversationId, Copilotmessage);

    return copilotResponseText;
  } else if(AIProcess === "Clause Summary"){
    const truncatedText = documentText;
    const userInput = `
    Only extract the clauses or terms as keys from the document.
    Return strictly as a valid raw JSON object, not as a string.
    The format must be:
    {
        "1. <key_name>": "<short summary of what this key represents>",
        "2. <key_name>": "<short summary of what this key represents>"
      }
    - Do not wrap the entire JSON object in quotes.
    - Do not return markdown code blocks like \`\`\`.
    - Return only the JSON object, exactly as specified above.
    `.trim();

    const instruction = `You are a legal assistant AI.
      User instruction: "${userInput?.trim()}"
      Use the contract text below to respond accurately and only based on the instruction.
      Contract Text: ${truncatedText}`.trim();

    const Copilotmessage = {
      type: "message",
      from: { id: "user", name: "You" },
      text: instruction,
      time: new Date().toISOString(),
    };

    const copilotResponseText = await generateCopilot(accessToken, conversationId, Copilotmessage);

    return copilotResponseText;
  } else if(AIProcess === "Alternate Clause"){
    const truncatedText = documentText;
    const userInput = `
    Read the given text and generate the alternatives of the text as a string.
    The format must be:
     [
        "<alternative version>",
        "<alternative version>",
        "<alternative version>",
        "<alternative version>"
      ]
    - Each alternative must be unique.
    - alternative should not include or at starting.
    - Do not return markdown code blocks like \`\`\`.
    `.trim();

      const instruction = `You are a legal assistant AI.
      User instruction: "${userInput}"
      Clause Text: ${truncatedText}`;

    const Copilotmessage = {
      type: "message",
      from: { id: "user", name: "You" },
      text: instruction,
      time: new Date().toISOString(),
    };

    const copilotResponse = await generateCopilot(accessToken, conversationId, Copilotmessage);

    return copilotResponse;
  } 

  return null;
}
